import { useEffect, useRef, useState } from "react";
import { FlatList, View } from "react-native";
import { Text } from "react-native-paper";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useController, UseControllerProps, useForm } from "react-hook-form";
import validator from "validator";

import { ScreenProps } from "../types";
import {
  Button,
  ScreenContainer,
  ScrollableContainer,
  ActionsContainer,
  KeyboardAvoidingView,
  TextInput,
  MaskedTextInput,
  TextInputProps,
  useErrorScroll,
} from "../../components";
import { JobApplyFormData } from "./types";
import DocumentUpload from "./DocumentUpload";
import TermsAndConditions from "./TermsAndConditions";
import { useApolloClient } from "@apollo/client";
import { V3_CLIENT_NAME } from "../../components/GraphqlProvider";
import { SUBMIT_JOB_APPLICATION } from "./mutations";
import { CURRENT_MEMBER_FRAGMENT, CurrentMember } from "../../screens/fragments";
import { uploadFile } from "./helpers";

const defaultValues: JobApplyFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  website: "",
  terms: false,
  resume: undefined,
  coverLetter: undefined,
};

function TextField({
  control,
  name,
  label,
  rules,
  ...inputProps
}: UseControllerProps<JobApplyFormData, "email" | "website" | "firstName" | "lastName" | "phone"> & {
  label: string;
} & TextInputProps) {
  const {
    field: { onChange: onChangeText, ...field },
    fieldState: { error },
  } = useController({ control, name, rules });

  return (
    <View className="mt-2 mb">
      <TextInput
        {...{
          onChangeText,
          error,
          label,
        }}
        {...inputProps}
        {...field}
        testID={`job-apply-form-input-${name}`}
      />
    </View>
  );
}

export const CURRENT_MEMBER = gql`
  query intercomQuery {
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface Query {
  currentMember: CurrentMember;
}

export default function JobApplyScreen({ route, navigation }: ScreenProps<"JobApply">) {
  const { id: jobId, companyName } = route.params;

  const { data } = useQuery<Query>(CURRENT_MEMBER, { fetchPolicy: "cache-first" });

  const client = useApolloClient();

  const [submitJobApplication, { error, loading }] = useMutation(SUBMIT_JOB_APPLICATION, {
    context: { clientName: V3_CLIENT_NAME },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setFocus,
    setValue,
    watch,
  } = useForm<JobApplyFormData>({ defaultValues });

  const flatListRef = useRef<FlatList>(null);

  const [errorMessage, setErrorMessage] = useState("");

  const { Divider, onSubmitError } = useErrorScroll({
    defaultValues,
    flatListRef,
  });

  async function onSubmit({ firstName, lastName, ...formData }: JobApplyFormData) {
    try {
      const attachments = (
        await Promise.all([
          formData.resume && uploadFile(formData.resume, client, "resume"),
          formData.coverLetter && uploadFile(formData.coverLetter, client, "cover_letter"),
        ])
      ).filter(Boolean);

      const input = {
        jobId,
        memberNumber: data?.currentMember.id,
        firstName,
        lastName,
        emailAddress: formData.email,
        phoneNumber: formData.phone.length ? formData.phone : undefined,
        website: formData.website.length ? formData.website : undefined,
        attachments,
      };

      await submitJobApplication({ variables: { input } });

      if (error) throw error;

      navigation.navigate("JobApplySuccess", { companyName });
    } catch (error) {
      setErrorMessage((error as Error).message || "Something went wrong");
    }
  }

  const next = (field: keyof JobApplyFormData) => () => {
    setFocus(field);
  };

  useEffect(() => {
    if (data?.currentMember) {
      setValue("firstName", data.currentMember.firstName);
      setValue("lastName", data.currentMember.lastName);
      if (data.currentMember.primaryEmail) setValue("email", data.currentMember.primaryEmail);
    }
  }, [data?.currentMember.lastName, data?.currentMember.firstName, data?.currentMember.primaryEmail]);

  return (
    <ScreenContainer>
      <KeyboardAvoidingView>
        <ScrollableContainer ref={flatListRef} keyboardShouldPersistTaps="handled">
          <Divider fields={["firstName", "lastName"]} hidden />

          <View className="px-6 py-8">
            <Text className="text-regular-tight font-700 mb-3">Personal Details</Text>

            <TextField
              control={control}
              label="First Name"
              name="firstName"
              onSubmitEditing={next("lastName")}
              returnKeyType="next"
              rules={{ required: "first name is required" }}
            />

            <TextField
              control={control}
              label="Last Name"
              name="lastName"
              onSubmitEditing={next("email")}
              returnKeyType="next"
              rules={{ required: "last name is required" }}
            />
          </View>

          <Divider fields={["email", "phone", "website"]} />
          <View className="px-6 pt-7 pb-8">
            <Text className="text-regular-tight font-700 mb-3">Contact Information</Text>

            <TextField
              autoCapitalize="none"
              control={control}
              keyboardType="email-address"
              label="Email Address"
              name="email"
              onSubmitEditing={next("phone")}
              returnKeyType="next"
              rules={{
                required: "email is required",
                validate: (value: string) => validator.isEmail(value) || "must be a valid email",
              }}
            />

            <TextField
              control={control}
              keyboardType="phone-pad"
              label={`Phone Number${watch("phone") ? "" : " (Optional)"}`}
              name="phone"
              render={({ onChangeText, ...props }) => (
                <MaskedTextInput
                  mask="999-999-9999"
                  {...props}
                  onChangeText={(text) => {
                    onChangeText?.(text);
                  }}
                />
              )}
              rules={{
                validate: (value: string) => (value ? validator.isMobilePhone(value) || "must be a valid phone number" : true),
              }}
            />

            <TextField
              autoCapitalize="none"
              control={control}
              keyboardType="url"
              label={`Website${watch("website") ? "" : " (Optional)"}`}
              name="website"
              rules={{
                validate: (value: string) => (value ? validator.isURL(value) || "must be a valid website" : true),
              }}
            />
          </View>

          <Divider fields={["resume", "coverLetter"]} />

          <DocumentUpload control={control} />

          <Divider field="terms" />

          <TermsAndConditions control={control} />
          <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
            <Button
              mode="contained"
              disabled={isSubmitting}
              loading={loading}
              isValid={isValid}
              onPress={handleSubmit(onSubmit, onSubmitError)}
            >
              Submit
            </Button>
          </ActionsContainer>
        </ScrollableContainer>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
